import { Bell } from 'lucide-react';
import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/shared/lib/cn';

export interface NotificationBellProps extends Omit<ComponentPropsWithRef<'button'>, 'children'> {
  /** Liczba nieprzeczytanych powiadomien. Powyzej `maxCount` pokazuje „9+”. */
  count?: number;
  maxCount?: number;
}

/**
 * Dzwonek powiadomien z licznikiem — jak w prawym gornym rogu centrum turnieju.
 *
 * Licznik jest opisany slownie dla czytnikow ekranu, zeby uzytkownik uslyszal
 * „Powiadomienia, 3 nieprzeczytane”, a nie samo „3”.
 */
export function NotificationBell({
  count = 0,
  maxCount = 9,
  className,
  ...props
}: NotificationBellProps) {
  const hasUnread = count > 0;
  const displayed = count > maxCount ? `${maxCount}+` : String(count);

  return (
    <button
      type="button"
      aria-label={
        hasUnread ? `Powiadomienia, ${count} nieprzeczytanych` : 'Powiadomienia, brak nowych'
      }
      className={cn(
        'border-border-strong bg-surface text-fg-muted relative inline-flex size-10 shrink-0',
        'items-center justify-center rounded-lg border shadow-xs transition-colors',
        'hover:bg-surface-hover hover:text-fg',
        className,
      )}
      {...props}
    >
      <Bell aria-hidden="true" className="size-[1.125rem]" />

      {hasUnread && (
        <span
          aria-hidden="true"
          className={cn(
            'bg-danger-500 absolute -top-1.5 -right-1.5 grid min-w-5 place-items-center',
            'ring-canvas rounded-full px-1.5 py-0.5 text-[0.625rem] font-bold text-white ring-2',
          )}
        >
          {displayed}
        </span>
      )}
    </button>
  );
}
